import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "@api/api";

import {
  ClientsByCategoryRow,
  ClientsCountUpdate,
  FinancialPage,
  NfeFileResponse,
  NfeNoteResponse,
  RebuildPayload,
  RebuildResponse,
  VolumetryAgreementHistoryEvent,
  VolumetryEmail,
  VolumetryEmailHistoryEvent,
  VolumetryEmailPatch,
} from "./types";

export const financialQueryKey = "financial_admin";

export async function listFinancialService<T>(
  endpoint: string,
  params: Record<string, string | number>
): Promise<FinancialPage<T>> {
  const { data } = await api.get<FinancialPage<T>>(`v1/financial_admin/${endpoint}/`, { params });
  return data;
}

export function useFinancialListQuery<T>(
  endpoint: string,
  params: Record<string, string | number>,
  enabled = true
) {
  return useQuery({
    queryKey: [financialQueryKey, endpoint, params],
    queryFn: () => listFinancialService<T>(endpoint, params),
    enabled,
  });
}

export async function rebuildFinancialService(payload: RebuildPayload): Promise<RebuildResponse> {
  const { data } = await api.post<RebuildResponse>("v1/financial_admin/rebuild/", payload);
  return data;
}

export async function patchClientsByCategoryService(
  params: Record<string, string | number>,
  body: ClientsCountUpdate
): Promise<ClientsByCategoryRow> {
  const { data } = await api.patch<ClientsByCategoryRow>(
    "v1/financial_admin/clients_by_category/",
    body,
    { params }
  );
  return data;
}

export function useRebuildFinancialMutation() {
  return useMutation({
    mutationFn: rebuildFinancialService,
  });
}

export async function getNfeNoteService(
  params: Record<string, string | number>
): Promise<NfeNoteResponse> {
  const { data } = await api.get<NfeNoteResponse>("v1/financial_admin/nfe/note/", { params });
  return data;
}

export function useNfeNoteQuery(params: Record<string, string | number> | null) {
  return useQuery({
    queryKey: [financialQueryKey, "nfe", "note", params],
    queryFn: () => getNfeNoteService(params as Record<string, string | number>),
    enabled: params !== null,
    retry: false,
  });
}

export async function getNfeFileService(
  params: Record<string, string | number>
): Promise<NfeFileResponse> {
  const { data } = await api.get<NfeFileResponse>("v1/financial_admin/nfe/file/", { params });
  return data;
}

export function useNfeFileMutation() {
  return useMutation({
    mutationFn: getNfeFileService,
  });
}

export async function openVolumetryEmailService(body: {
  agreement_id: number;
  year: number;
  month: number;
}): Promise<VolumetryEmail> {
  const { data } = await api.post<VolumetryEmail>("v1/financial_admin/volumetry_email/", body);
  return data;
}

export async function patchVolumetryEmailService(
  id: number,
  body: VolumetryEmailPatch | FormData
): Promise<VolumetryEmail> {
  const { data } = await api.patch<VolumetryEmail>(
    `v1/financial_admin/volumetry_email/${id}/`,
    body,
    body instanceof FormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : undefined
  );
  return data;
}

export async function refreshVolumetryEmailService(id: number): Promise<VolumetryEmail> {
  const { data } = await api.post<VolumetryEmail>(`v1/financial_admin/volumetry_email/${id}/refresh/`);
  return data;
}

export async function resetVolumetryEmailService(id: number): Promise<VolumetryEmail> {
  const { data } = await api.post<VolumetryEmail>(`v1/financial_admin/volumetry_email/${id}/reset/`);
  return data;
}

export async function sendVolumetryEmailService(id: number): Promise<VolumetryEmail> {
  const { data } = await api.post<VolumetryEmail>(`v1/financial_admin/volumetry_email/${id}/send/`);
  return data;
}

export async function getVolumetryEmailHistoryService(
  id: number
): Promise<VolumetryEmailHistoryEvent[]> {
  const { data } = await api.get<VolumetryEmailHistoryEvent[]>(
    `v1/financial_admin/volumetry_email/${id}/history/`
  );
  return data;
}

export async function getVolumetryAgreementHistoryService(
  agreementId: number
): Promise<VolumetryAgreementHistoryEvent[]> {
  const { data } = await api.get<VolumetryAgreementHistoryEvent[]>(
    "v1/financial_admin/volumetry_email/history/",
    { params: { agreement_id: agreementId } }
  );
  return data;
}

export async function patchVolumetryEmailFileService(
  emailId: number,
  fileId: number,
  include: boolean
): Promise<VolumetryEmail> {
  const { data } = await api.patch<VolumetryEmail>(
    `v1/financial_admin/volumetry_email/${emailId}/files/${fileId}/`,
    { include }
  );
  return data;
}

export async function deleteVolumetryEmailFileService(
  emailId: number,
  fileId: number
): Promise<VolumetryEmail> {
  const { data } = await api.delete<VolumetryEmail>(
    `v1/financial_admin/volumetry_email/${emailId}/files/${fileId}/`
  );
  return data;
}
